import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { AbstractElementCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to adjust the end of an element (both extending and trimming)
 * Handles both shrinking and extending the duration by changing the element's end time
 *
 * This command replaces the functionality of both the former TrimElementEndCommand
 * and ExtendElementCommand, unifying them into a single implementation that handles
 * both cases:
 * - Extending mode: When new end time > current end time
 * - Trimming mode: When new end time < current end time
 *
 * The command detects automatically which behavior to use based on the comparison
 * between the current element end time and the requested new end time.
 *
 * Features:
 * - Frame snapping of the end time based on editor framerate
 * - Full ripple mode support to push or pull subsequent elements
 * - Video-specific property handling (trim_duration)
 * - State capture for undo/redo
 *
 * Uses direct object references
 */
export class TrimElementEndCommand extends AbstractElementCommand {
  private newEndTime: number;
  private snapToFrame: boolean;

  getName(): string {
    return 'TrimElementEndCommand';
  }
  private originalState: Record<string, any>;
  private originalEndTime: number;
  /**
   * Creates a new TrimElementEndCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newEndTime The new end time for the element (in seconds)
   * @param snapToFrame Whether to snap the end time to frame boundaries (default: true)
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newEndTime: number,
    snapToFrame: boolean = true,
  ) {
    super(editor, element);

    // Apply frame snapping to the end time if enabled
    this.newEndTime = snapToFrame
      ? this.snapTimeToFrame(newEndTime)
      : newEndTime;
    this.snapToFrame = snapToFrame;

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);
    this.originalEndTime =
      this.sourceElement.time +
      (typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0);

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    const isTranscriptElem = this.sourceElement instanceof VideoTranscriptElement;
    const result = this.performTrimOperation();

    if (result) {
      this.recalculateTimeline();

      // Reconcile karaoke track if transcript element was trimmed
      if (isTranscriptElem) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
      }
    }

    return result;
  }

  /**
   * Performs the trim operation using the appropriate track manager
   * @returns true if the operation was successful
   */
  private performTrimOperation(): boolean {
    if (this.isTranscriptElement()) {
      return this.trimTranscriptElement();
    } else {
      return this.trimRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Trims a VideoTranscriptElement using TranscriptTrackManager
   */
  private trimTranscriptElement(): boolean {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    const clipResult = transcriptTrackManager.trimElementEnd(
      this.sourceElement as VideoTranscriptElement,
      this.newEndTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
    return clipResult !== null;
  }

  /**
   * Trims a regular element using TrackManager
   */
  private trimRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.trimElementEnd(
      this.sourceElement,
      this.newEndTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    // Change description based on whether extending or trimming
    const currentEndTime =
      this.sourceElement.time +
      (typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0);

    if (this.newEndTime > currentEndTime) {
      return `Extend element ${this.sourceElement.id} to ${this.newEndTime}s`;
    }
    return `Trim end of element ${this.sourceElement.id} to ${this.newEndTime}s`;
  }
}
